import React, { useCallback, useMemo, useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { faCaretDown, faLock } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ViewportContext from "~/common/ViewportContext";
import { environment } from "~/models";
import { useModals } from "~/ui";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import Avatar from "~/ui/legacy/Avatar";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";
import Tippy from "~/ui/Tooltip";

import { RemoveAccessModal } from "./RemoveAccessModal";

import type { ShareModalQuery$data, StreamAccessType } from "~/__generated__/ShareModalQuery.graphql";
import type { UserAccessRowUnsharingMutation } from "~/__generated__/UserAccessRowUnsharingMutation.graphql";

import styles from "../css/ShareModal.module.css";

const UnshareMutation = graphql`
	mutation UserAccessRowUnsharingMutation($id: ID!, $userId: ID!) {
		noteStreamUnshare(id: $id, userId: $userId) {
			noteStreamId
			updatedStreams {
				id
				index
			}
			noteStream {
				id
				index
				userCount
				notestreamusers {
					id
					accessType
					user {
						id
						email
						image
						fullName
						lastName
						firstName
						isActive
						isConfigured
						isGuest
					}
				}

				notes(first: 3) {
					edges {
						node {
							id
							noteuserSet {
								id
							}
						}
					}
				}
			}
		}
	}
`;

interface UserAccessRowProps {
	user: UserAttrs;
	viewer: ShareModalQuery$data["viewer"];
	isViewer: boolean;
	isOwner: boolean;
	readOnly: boolean;
	streamId: string;
	accessType: StreamAccessType;
	onRemove(userId: string): void;
}

export default function UserAccessRow(props: UserAccessRowProps) {
	const { user, viewer, isViewer, isOwner, streamId, readOnly, accessType, onRemove } = props;
	const { isMobile } = React.useContext(ViewportContext);
	const { isConfigured } = user;
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const notRemovable = useMemo(
		() => viewer.user.isGuest || accessType !== "shared" || isOwner,
		[viewer, accessType, isOwner],
	);
	const lockTooltip = useMemo(() => {
		if (readOnly) {
			return "You only have access to some notes and cannot change user permissions";
		}
		if (isOwner) {
			return "This member is locked because they are the creator of this stream";
		}
		if (viewer.user.isGuest) {
			return "You cannot remove users as a Guest to this workspace";
		}
		if (accessType === "partial") {
			return "This person is locked because they have partial access to the meeting series";
		}
		return "This member is locked because they are an attendee of all events in this stream";
	}, [readOnly, isOwner, viewer, accessType]);
	const modals = useModals();

	const removeUserAccess = useCallback(async () => {
		const result = await modals.open<boolean>(({ close }) => (
			<RemoveAccessModal onClose={close} userName={user.fullName || ""} isSelf={isViewer} />
		));
		if (!result) return;

		commitMutation<UserAccessRowUnsharingMutation>(environment, {
			mutation: UnshareMutation,
			variables: {
				id: streamId,
				userId: user.id,
			},
			onCompleted: (response, errors) => {
				if (errors) {
					toast.error("Failed to remove user, please try again later");
					return;
				}
				onRemove(user.id);
			},
		});
	}, [modals, user, streamId, isViewer, onRemove]);

	return (
		<>
			<div className={styles.userRow}>
				<Avatar user={user} width="24px" className={styles.userRowAvatar} />
				<span className={styles.nameSection}>
					<span className={styles.name}>{user.fullName}</span>
					{isViewer && <span className={styles.youIndicator}>&nbsp;(You)</span>}
					{!isConfigured && (
						<span className={styles.pendingInvite}>&nbsp;(Pending{isMobile || <>: Invite sent</>})</span>
					)}
				</span>
				{readOnly || notRemovable ? (
					<Tippy placement="top" content={lockTooltip} maxWidth={150} interactive={true}>
						<span className={styles.lockedAccess}>
							Can edit <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
						</span>
					</Tippy>
				) : (
					<Menu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
						<Menu.Trigger>
							<button className={styles.accessMenuTrigger}>
								Can edit
								<FontAwesomeIcon icon={faCaretDown} className={styles.menuTriggerCaret} />
							</button>
						</Menu.Trigger>
						<Menu.Content data-ignore-focus-lock>
							<Menu.RadioGroup value="EDIT" data-ignore-focus-lock>
								<Menu.RadioItem value="EDIT">Can edit</Menu.RadioItem>
							</Menu.RadioGroup>
							<Menu.Separator />
							<Menu.Item destructive onSelect={removeUserAccess}>
								{isViewer ? "Leave" : "Remove"}...
							</Menu.Item>
						</Menu.Content>
					</Menu>
				)}
			</div>
		</>
	);
}
